/**
 * The robots-decision mints — the only four ways a page can obtain the
 * `RobotsDecision` that `SeoHead` requires (ADR 0142, V8-proper).
 *
 * `SeoMeta.robots` used to be an optional `RobotsPolicy`, so `undefined` meant
 * "indexable" and 53 of 58 `<SeoHead` call sites were indexable because nobody
 * typed anything. Making the field required would have fixed the omission but
 * created a worse problem: 58 hand-written indexability claims, i.e. a second
 * source of truth next to `manifests/routes.yaml`, which already carries
 * `indexable: true|false` per route and is what the sitemap and robots.txt
 * pipelines read.
 *
 * So the value is DERIVED instead. Each mint below takes its policy from a
 * source of truth and none of them accepts a policy as an argument:
 *
 *   - [`routeRobots`]   — a static route's `indexable:` flag, via the generated
 *                         `static-seo.gen.ts` registry (used by `useStaticSeo`,
 *                         so every indexable static page gets it with no
 *                         per-page code at all);
 *   - [`noIndexRoute`]  — a route id the SSOT declares NON-indexable. The
 *                         argument type is the generated `NonIndexableRouteId`
 *                         union, so a page can only declare itself noindex by
 *                         naming a route routes.yaml agrees is noindex, and
 *                         flipping that flag breaks the page's build;
 *   - [`resourceRobots`]— the backend's per-entity `SeoData.robots` (called by
 *                         `seoFromProto` only);
 *   - [`undeterminedRobots`] — the DEGRADATION state (see below);
 *   - [`ERROR_SHELL_ROBOTS`] — the root ErrorBoundary, which is not a route.
 *
 * There is deliberately NO mint that takes a `RobotsPolicy`: "this page is
 * indexable" is not a sentence a page component is allowed to write.
 */
import type {
  ErrorShellRobots,
  ResourceRobots,
  RobotsPolicy,
  RouteRobots,
  UndeterminedRobots,
} from '../../types/seo';
import {
  NON_INDEXABLE_ROUTE_ROBOTS,
  staticSeo,
  type NonIndexableRouteId,
  type StaticSeoRoutePath,
} from './static-seo.gen';

/**
 * The robots decision `manifests/routes.yaml` declares for a static route,
 * keyed by the same canonical path `useStaticSeo` builds canonical/hreflang
 * from — so a page's robots tag and its canonical URL are read out of the same
 * manifest row and cannot disagree.
 *
 * The `policy: RobotsPolicy` annotation is load-bearing, not decoration: it is
 * what ties the GENERATED route-policy vocabulary (`RouteRobotsPolicy` — what
 * an `indexable:` flag can mean) to the hand-written rendering one (what
 * `<meta name="robots">` can say). A generator that emitted a spelling
 * `SeoHead` cannot render would fail to compile here rather than silently drop
 * the tag.
 */
export function routeRobots(path: StaticSeoRoutePath): RouteRobots {
  const policy: RobotsPolicy = staticSeo[path].robots;
  return { source: 'route', policy, route: path } as RouteRobots;
}

/**
 * The robots decision for a route the SSOT declares NON-indexable — the
 * account/wizard/conversations surfaces, the inquiry form, the staff post
 * preview.
 *
 * The id is checked against the generated `NonIndexableRouteId` union, so:
 *   - a page cannot mint a noindex for a route routes.yaml says is indexable
 *     (there is no such member), and
 *   - flipping a route to `indexable: true` deletes its member and the page
 *     that declared itself noindex STOPS COMPILING — the contradiction between
 *     page and sitemap becomes a build error instead of a crawl finding.
 */
export function noIndexRoute(id: NonIndexableRouteId): RouteRobots {
  const policy: RobotsPolicy = NON_INDEXABLE_ROUTE_ROBOTS[id];
  return { source: 'route', policy, route: id } as RouteRobots;
}

/**
 * The backend's PER-ENTITY decision, read off `SeoData.robots`. Called only by
 * `seoFromProto` — this is the arm that carries `noindex,follow` (a thin
 * district facet, page 2 of a directory), which no route-level flag can express.
 */
export function resourceRobots(policy: RobotsPolicy): ResourceRobots {
  return { source: 'resource', policy } as ResourceRobots;
}

/**
 * DEGRADATION — the backend response carried no `SeoData`, so this render's
 * indexability is UNKNOWN.
 *
 * Fail-closed is right (an indexable page with no SEO data is worse than a
 * de-indexed one, ADR 0142 / V9), but until now the fail-closed value was
 * `'noindex-nofollow'` — byte-identical to a deliberate noindex. That is what
 * made the `/partners` oscillation invisible: `ListPartners` degrading
 * de-indexed the entire partner directory in all three locales, then healed,
 * and the only trace was two consecutive Ahrefs crawls disagreeing.
 *
 * `missingField` is derived from the generated message descriptor of whichever
 * proto message SHOULD have carried the data, so the signal names a real field
 * and cannot go stale when the proto is renamed. `createLoader` turns this
 * value into a level-50 log naming the route and the field.
 */
export function undeterminedRobots(missingField: string): UndeterminedRobots {
  return {
    source: 'undetermined',
    policy: 'noindex-nofollow',
    missingField,
  } as UndeterminedRobots;
}

/**
 * The root ErrorBoundary's decision. Not a route: the boundary renders for URLs
 * that matched no route at all, so it cannot derive from routes.yaml. `noindex,
 * FOLLOW` — de-index the error shell, but keep crawling the links on it (the
 * 404 page links home).
 */
export const ERROR_SHELL_ROBOTS: ErrorShellRobots = {
  source: 'error-shell',
  policy: 'noindex-follow',
} as ErrorShellRobots;

/**
 * Find a degradation report inside a loader's returned data, if it carries one.
 *
 * Loaders return `{ …, seo: SeoMeta }`; this scans the returned record's own
 * values for a `SeoMeta`-shaped one whose robots decision is `undetermined`, so
 * the reporting seam (`createLoader`) needs no per-loader wiring and a new
 * SEO-bearing loader is covered the day it is written. Structural rather than
 * nominal because loader return types are inferred, not declared — but the
 * VALUE it looks for is mintable in exactly one place (`undeterminedRobots`),
 * so a false positive is not constructible.
 */
export function degradedSeoOf(data: Record<string, unknown>): UndeterminedRobots | undefined {
  for (const value of Object.values(data)) {
    if (typeof value !== 'object' || value === null) continue;
    const robots: unknown = (value as { robots?: unknown }).robots;
    if (
      typeof robots === 'object' &&
      robots !== null &&
      (robots as { source?: unknown }).source === 'undetermined'
    ) {
      return robots as UndeterminedRobots;
    }
  }
  return undefined;
}
